import type { AccountConfirmationSent } from '@chukua/common';
import { useRef, useState } from 'react';
import { useSubmission } from '../../../shared/hooks/use-submission';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { confirmAccount, sendAccountConfirmation } from '../account.api';
import { isConfirmationRequired } from '../policies/confirmation';

/** `purpose` finishes "To …, enter the code": "change your password". */
export type ConfirmationState = { phase: 'idle' } | { phase: 'code'; purpose: string; sent: AccountConfirmationSent };

export type Confirmation = ReturnType<typeof useConfirmation>;

/**
 * "Confirm it's you": a sensitive change is tried as is, and when the server answers that the session needs confirming,
 * a code goes to the person's own email or mobile, the panel asks for it, and the change is tried again once it's right.
 * `confirmed` resolves to undefined when the person cancels, so the caller treats it as nothing happened.
 */
export function useConfirmation() {
  const [state, setState] = useState<ConfirmationState>({ phase: 'idle' });
  const settle = useRef<((isConfirmed: boolean) => void) | null>(null);
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  async function confirmed<T>(purpose: string, request: () => Promise<T>): Promise<T | undefined> {
    try {
      return await request();
    } catch (err) {
      if (!isConfirmationRequired(err)) throw err;
    }
    const sent = await sendAccountConfirmation();
    clearError();
    setState({ phase: 'code', purpose, sent });
    const isConfirmed = await new Promise<boolean>((resolve) => {
      settle.current = resolve;
    });
    return isConfirmed ? request() : undefined;
  }

  function close(isConfirmed: boolean) {
    settle.current?.(isConfirmed);
    settle.current = null;
    setState({ phase: 'idle' });
    clearError();
  }

  async function confirm(code: string) {
    const isConfirmed = await submit(
      () => confirmAccount(code).then(() => true),
      (err) => apiErrorMessage(err) ?? 'Incorrect code. Check the newest one and try again.',
    );
    if (isConfirmed) close(true);
  }

  async function resend() {
    if (state.phase !== 'code') return;
    const sent = await submit(
      () => sendAccountConfirmation(),
      (err) => apiErrorMessage(err) ?? "Couldn't send another code. Try again.",
    );
    if (!sent) return;
    setState({ ...state, sent });
    toast.success(sent.displayedCode ? 'New code shown' : 'New code sent');
  }

  return { state, error, submitting, confirmed, confirm, resend, cancel: () => close(false) };
}
